import { LoadingService } from './../../../services/loading.service';
import { BountyResolver } from './../../../resolvers/bounty.resolver';
import { Subscription } from 'rxjs';
import { UserService } from './../../../services/user.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Bounty } from '../../../interfaces/Bounty';
import { BountyService } from '../../../services/bounty.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Comment } from '../../comments/comments-section/comments-section.component';
import { CLAIM_STATUS_COLORS } from '../../view-claim/view-claim.component';
import { SuiModalService } from 'ng2-semantic-ui';
import { ConfirmModal } from '../../confirm-modal/confirm-modal.component';

export const STATUS_COLORS = {
  new: 'green',
  'winner-chosen': 'blue',
  complete: 'green',
};

export const getBeatmapLink = (bounty: Bounty) => {
  return `https://osu.ppy.sh/beatmapsets/${bounty.beatmapset_id}#${
    bounty.mode === 'ctb' ? 'fruits' : bounty.mode
    }/${bounty.beatmap_id}`;
};

@Component({
  selector: 'app-view-bounty',
  templateUrl: './view-bounty.component.html',
  styleUrls: ['./view-bounty.component.scss'],
})
export class ViewBountyComponent implements OnInit, OnDestroy {
  public bounty: Bounty;
  public comments: Comment[];
  public showClaimBountyForm = false;
  public user: any;
  public isOwner: boolean;
  public statusColors = STATUS_COLORS;
  public createdClaimId: number;
  public isMod: boolean;
  private subscriptions: Subscription[] = [];
  public showAddToPoolForm: boolean;
  public amountToAdd: number;

  constructor(
    private bountyService: BountyService,
    private route: ActivatedRoute,
    private userService: UserService,
    private router: Router,
    private modalService: SuiModalService,
    private bountyResolver: BountyResolver,
    private loadingService: LoadingService,
  ) {  }

  ngOnInit() {
    this.user = this.userService.getUser();
    this.isMod = this.user && this.user.isMod;
    this.subscriptions.push(
      this.route.data.subscribe(({ data }) => {
        this.mapDataToState(data);
      }),
    );
  }

  private mapDataToState(data) {
    const { bounty, comments } = data;

    this.bounty = bounty;
    this.comments = comments;
    this.isOwner = this.user && bounty && bounty.user_id === this.user.id;
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get title() {
    return this.bountyService.getBountyTitle(this.bounty);
  }

  get labelClass() {
    return this.bountyService.getBountyLabelClass(this.bounty);
  }

  get canDeleteBounty() {
    return (this.isOwner && this.bounty.status === 'new') || this.isMod;
  }

  get canEditBounty() {
    const hasActiveClaims =
      this.bounty.claims &&
      this.bounty.claims.some(claim => !['withdrawn', 'denied'].includes(claim.status));
    return (
      (this.isOwner && this.bounty.status === 'new' && !hasActiveClaims) ||
      this.isMod
    );
  }

  get userCanClaim() {
    return (
      this.user &&
      !this.isOwner &&
      !this.bounty.claims.some(
        claim =>
          (claim.user_id === this.user.id && ['new'].includes(claim.status)) ||
          claim.status === 'accepted',
      )
    );
  }

  get canViewOwnClaim() {
    return (
      !this.isOwner &&
      this.bounty &&
      this.bounty.userHasClaimed &&
      this.userClaim
    );
  }

  get canViewWinningClaim() {
    return (
      this.isOwner &&
      this.bounty.claims.some(claim => claim.status === 'accepted')
    );
  }

  get winningClaim() {
    return this.bounty.claims.find(claim => claim.status === 'accepted');
  }

  async deleteBounty() {
    this.modalService
      .open(
        new ConfirmModal(
          'Delete Bounty',
          'Are you sure you want to delete this bounty?',
        ),
      )
      .onApprove(async () => {
        await this.bountyService.deleteBounty(this.bounty.id);
        this.router.navigate(['/']);
      })
      .onDeny(() => { });
  }

  get beatmapHref() {
    return getBeatmapLink(this.bounty);
  }

  get userClaim() {
    if (!this.user) {
      return;
    }

    return this.bounty.claims.find(claim => (claim.user_id = this.user.id));
  }

  public async refetch() {
    const data = await this.loadingService.loadWhileFetching(
      this.bountyResolver.resolve(this.route.snapshot),
    );

    this.mapDataToState(data);
  }

  public onBeatmapHrefClicked(event) {
    event.stopPropagation();
  }

  public getStatusColor(claim: { status: string }) {
    return CLAIM_STATUS_COLORS[claim.status];
  }

  public isClaimer(claim) {
    return this.user && this.user.id === claim.user_id;
  }

  public goToClaim(claim) {
    if (!this.isOwner && !this.isClaimer(claim) && !this.isMod) {
      return;
    }

    this.router.navigate(['/claims/' + claim.id]);
  }

  public editBounty() {
    this.router.navigate(['/bounties/' + this.bounty.id + '/edit']);
  }

  public async onClaimCreated(claimId: number) {
    await this.refetch();
    this.createdClaimId = claimId;
  }

  get hasNewClaims() {
    return this.bounty.claims.some(claim => claim.status === 'new');
  }

  get amountValid() {
    return (typeof this.amountToAdd === 'number') && this.amountToAdd > 1 && this.amountToAdd < 1000;
  }

  public parseReward(amount: number): number {
    return parseFloat(amount.toFixed(2));
  }

  public claimBountyAction() {
    this.showClaimBountyForm = true;
    setTimeout(() => {
      document.getElementById('claim-reward-section').scrollIntoView();
    }, 100);
  }
}
